import { styled } from '@mui/material/styles';
import { Box, Button, Typography } from '@mui/material';
import { keyframes } from '@mui/system';

// Keyframes definitions
const textwipeIn = keyframes`
  0% {
    -webkit-mask-position: 100% 100%;
  }
  100% {
    -webkit-mask-position: 0% 0%;
  }
`;

const wipin = keyframes`
  0% {
    -webkit-mask-position: 100% 100%;
  }
  100% {
    -webkit-mask-position: 0% 0%;
  }
`;

const launchAway = keyframes`
  0% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  100% {
    opacity: 0;
    transform: translateY(-100px) scale(0.8);
  }
`;

const popRight = keyframes`
  0% {
    opacity: 0;
    transform: translateX(40px);
  }
  100% {
    opacity: 1;
    transform: translateX(0);
  }
`;

const zoomBounce = keyframes`
  0% {
    transform: scale(0.5);
    opacity: 0;
  }
  60% {
    transform: scale(1.05);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const cloudyFadeOutLeft = keyframes`
  0% {
    opacity: 1;
    filter: blur(0);
    transform: translateX(0);
  }
  100% {
    opacity: 0;
    filter: blur(10px);
  }
`;

const fadeInWord = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
    filter: blur(4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
    filter: blur(0);
  }
`;

const cloudyFadeLeft = keyframes`
  0% {
    opacity: 0;
    filter: blur(12px);
    transform: translateX(-40px);
  }
  50% {
    opacity: 0.6;
    filter: blur(6px);
    transform: translateX(-10px);
  }
  100% {
    opacity: 1;
    filter: blur(0);
    transform: translateX(0);
  }
`;

const glowPulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.6);
  }
  70% {
    box-shadow: 0 0 0 13px rgba(255, 255, 255, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0);
  }
`;

// Styled components for animations
export const TextWipeOut = styled(Typography)<{ $fadeOut?: boolean }>(({ $fadeOut }) => ({
  fontFamily: `'Fredoka', sans-serif`,
  whiteSpace: 'normal',
  fontSize: 'clamp(24px, 9vw, 48px)',
  fontWeight: 700,
  color: '#fff',
  textAlign: 'center',
  textShadow: '0px 4px 6px rgba(0, 0, 0, 0.3)',
  lineHeight: '1.2',
  fill: 'white',
  WebkitMaskImage: 'linear-gradient(135deg, transparent 60%, black 61%)',
  WebkitMaskSize: '300% 300%',
  WebkitMaskPosition: '100% 100%',
  WebkitMaskRepeat: 'no-repeat',
  ...(($fadeOut) && {
    animation: `${textwipeIn} 3s ease-in-out forwards`,
    animationFillMode: 'forwards',
  }),
}));

export const LogoWipeIn = styled(Box)({
  height: 'auto',
  fill: 'white',
  WebkitMaskImage: 'linear-gradient(135deg, black 60%, transparent 61%)',
  WebkitMaskSize: '300% 300%',
  WebkitMaskPosition: '100% 100%',
  WebkitMaskRepeat: 'no-repeat',
  animation: `${wipin} 3s ease-in-out forwards`,
  animationFillMode: 'forwards',
});

export const ButtonLaunch = styled(Button)({
  animation: `${launchAway} 600ms ease-in forwards`,
});

export const ButtonPopRight = styled(Button)({
  opacity: 0,
  transform: 'translateX(40px)',
  animation: `${popRight} 0.9s ease-out forwards`,
});

export const ButtonZoomBounce = styled(Button)<{ $animateIn?: boolean }>(({ $animateIn }) => ({
  opacity: 0,
  ...($animateIn && {
    animation: `${zoomBounce} 0.8s ease-out forwards`,
  }),
}));

export const CloudyFadeOutText = styled(Typography)({
  animation: `${cloudyFadeOutLeft} 1.5s ease-out forwards`,
  opacity: 1,
  fontSize: 'clamp(24px, 9vw, 48px)',
  fontWeight: 700,
  color: '#fff',
  textAlign: 'center',
  whiteSpace: 'normal',
});

export const FadingText = styled(Typography)({
  animation: `${cloudyFadeLeft} 1s ease-out forwards`,
  opacity: 0,
});

export const WordSpan = styled('span')({
  display: 'inline-block',
  opacity: 0,
  animationName: fadeInWord,
  animationDuration: '0.6s',
  animationTimingFunction: 'ease-out',
  animationFillMode: 'forwards',
  marginRight: '8px',
});

export const LetsGoButton = styled(Button)<{ $animateIn?: boolean }>(({ $animateIn }) => ({
  width: '158px',
  height: '44px',
  position: 'absolute',
  right: '0px',
  backgroundColor: '#4F49E1',
  color: '#fff',
  fontWeight: 700,
  paddingLeft: '25px',
  borderRadius: '40px',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  textTransform: 'uppercase',
  fontSize: '20px',
  whiteSpace: 'nowrap',
  letterSpacing: '-3%',
  border: '1px solid white',
  cursor: 'pointer',
  zIndex: 1,
  overflow: 'visible',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  opacity: 0,
  ...($animateIn && {
    animation: `${zoomBounce} 0.8s ease-out forwards`,
  }),
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    borderRadius: '40px',
    zIndex: -1,
    animation: `${glowPulse} 2s infinite ease-in-out`,
  },
  '&:hover': {
    backgroundColor: '#4F49E1',
  },
}));

export const IconWrapper = styled('span')({
  backgroundColor: '#1A73E8',
  borderRadius: '50%',
  width: '34px',
  height: '34px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  
  // Style the NextIcon child component
  '& > div': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '34px',
    height: '34px',
  },
});

// Container styled components
export const Container = styled(Box)({
  width: '100%',
  height: '100%',
  position: 'absolute',
  top: 0,
  left: 0,
  padding: '100px 20px',
  color: 'black',
});

export const RevealWrapper = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  transition: 'clip-path 0.6s ease-in-out',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 10,
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'cover',
});

export const CompanyName = styled(Typography)({
  color: 'white',
  fontSize: 'clamp(20px, 7vw, 40px)',
  fontWeight: 700,
  fontFamily: 'Poppins, sans-serif',
  textAlign: 'center',
});

export const NextButtonWrapper = styled(Box)({
  position: 'absolute',
  bottom: '17%',
  right: '10px',
  width: '90%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 10,
});

export const Logo = styled('img')({
  objectFit: 'contain',
  width: '90%',
  maxWidth: '520px',
  maxHeight: '256px',
});

export const NextButton = styled(Button)({
  color: 'white',
  border: '1px solid',
  width: '70%',
});
